import { useSearchParams } from 'react-router-dom';

import type { CategoryCardModel } from '@/features/catalog/api';
import { parseCatalogUrlState, updateCatalogParam } from '@/features/catalog/utils/catalogUrlState';

type CatalogControlsProps = {
  resultCount: number;
  categories?: CategoryCardModel[];
  fixedCategory?: string;
  updating?: boolean;
};

export function CatalogControls({
  resultCount,
  categories = [],
  fixedCategory,
  updating = false,
}: CatalogControlsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = parseCatalogUrlState(searchParams);
  const update = (key: Parameters<typeof updateCatalogParam>[1], value: string) =>
    setSearchParams(updateCatalogParam(searchParams, key, value));

  return (
    <div className="catalog-controls">
      <div className="catalog-controls__count" role="status" aria-live="polite">
        {resultCount} {resultCount === 1 ? 'product' : 'products'} loaded
        {updating && <span className="catalog-updating"> · Updating…</span>}
      </div>
      <div className="catalog-controls__fields">
        {!fixedCategory && (
          <label>
            <span>Category</span>
            <select
              aria-label="Category"
              value={state.category ?? ''}
              onChange={(event) => update('category', event.target.value)}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option value={category.slug} key={category.slug}>
                  {category.shortTitle}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>Product type</span>
          <select
            aria-label="Product type"
            value={state.productType ?? ''}
            onChange={(event) => update('productType', event.target.value)}
          >
            <option value="">All product types</option>
            <option value="wood-engraving">Wood engraving</option>
            <option value="ornament">Ornament</option>
            <option value="tshirt">T-shirt</option>
          </select>
        </label>
        <label>
          <span>Badge</span>
          <select
            aria-label="Badge"
            value={state.badge ?? ''}
            onChange={(event) => update('badge', event.target.value)}
          >
            <option value="">Any badge</option>
            <option value="bestseller">Bestseller</option>
            <option value="new">New</option>
            <option value="popular">Popular</option>
            <option value="seasonal">Seasonal</option>
          </select>
        </label>
        <label className="checkbox-control">
          <input
            type="checkbox"
            checked={state.featured === true}
            onChange={(event) => update('featured', event.target.checked ? 'true' : '')}
          />
          <span>Featured only</span>
        </label>
        <label>
          <span>Sort by</span>
          <select
            aria-label="Sort by"
            value={state.sort}
            onChange={(event) => update('sort', event.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
          </select>
        </label>
        <button className="text-button" type="button" onClick={() => setSearchParams({})}>
          Clear filters
        </button>
      </div>
    </div>
  );
}
